import { useEffect, useState } from 'react';

/**
 * Is the viewport narrower than Bootstrap's `md` breakpoint?
 *
 * One definition of "narrow" for the whole admin UI, so the pieces that change shape change
 * shape together: DataTable stacks its columns, AdminSidebar collapses to the icon rail, and a
 * nav group opens a landing page of cards rather than a submenu. Before this, the breakpoint was
 * written out twice as a `window.innerWidth < 768` plus a resize listener, and a third time as
 * Bootstrap's `d-md-none` utility classes.
 *
 * 767.98px rather than 768px because Bootstrap's own `max-width` queries stop just short of the
 * next breakpoint, so a viewport of exactly 768px is `md` to both this hook and the CSS.
 */
export const NARROW_VIEWPORT_QUERY = '(max-width: 767.98px)';

export default function useIsNarrowViewport() {
  const [isNarrow, setIsNarrow] = useState(() => matches());

  useEffect(() => {
    // No matchMedia in jsdom's older shims and in some embedded webviews; the hook then reports
    // "not narrow" and the UI keeps its wide layout, which is the safer of the two mistakes.
    const mediaQuery = window.matchMedia?.(NARROW_VIEWPORT_QUERY);
    if (!mediaQuery) return undefined;

    const handleChange = (event) => setIsNarrow(event.matches);

    // Re-read on mount: the width can have changed between the initial state and this effect.
    setIsNarrow(mediaQuery.matches);

    // `addEventListener` on a MediaQueryList is the modern form; Safari below 14 only has
    // `addListener`, and this app is used on installer phones that are not always new.
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return isNarrow;
}

function matches() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(NARROW_VIEWPORT_QUERY).matches;
}
